import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate, csvCell } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { CalendarClock, Check, Download } from "lucide-react";
import { OUTCOMES, lastLog, RefillLogSummary, RefillNoteButton } from "@/components/RefillNotes";

const day = (d) => fmtDate(`${d}T12:00:00`);

function exportCsv(rows) {
  const head = ["Patient", "Phone", "Medicine", "Note date", "Refill date", "Status", "Last follow-up", "Follow-up by", "Call back", "Called", "Notes"];
  const lines = [head.join(","), ...rows.map((r) => { const l = lastLog(r); return [r.patient?.name, r.phone, r.medicines.join("; "), r.date, r.refill_date,
    r.days_left < 0 ? `${-r.days_left} days overdue` : r.days_left === 0 ? "Due today" : `Due in ${r.days_left} day${r.days_left === 1 ? "" : "s"}`,
    l ? `${OUTCOMES[l.outcome]}${l.note ? ` - ${l.note}` : ""}` : "", l ? `${l.by} ${l.at.slice(0, 10)}` : "", r.callback_date || "", "", ""].map(csvCell).join(","); })];
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  Object.assign(document.createElement("a"), { href: url, download: `refill-call-list-${new Date().toISOString().slice(0, 10)}.csv` }).click();
  URL.revokeObjectURL(url);
}

const RefillRow = ({ r, onDone, busy }) => (
  <tr data-testid={`refill-row-${r.id}`} className="border-t border-slate-100">
    <td className="px-3 py-2 font-medium text-slate-800">{r.patient?.name}</td>
    <td className="hidden px-3 py-2 text-slate-600 sm:table-cell">{r.phone || "—"}</td>
    <td className="px-3 py-2 text-slate-600">{r.medicines.join(", ")}</td>
    <td className="hidden whitespace-nowrap px-3 py-2 text-slate-600 md:table-cell">{day(r.date)}</td>
    <td className="whitespace-nowrap px-3 py-2">
      <span className={`font-semibold ${r.days_left < 0 ? "text-red-600" : "text-amber-600"}`}>{day(r.refill_date)}</span>
      <span className="ml-1 text-xs text-slate-400">{r.days_left < 0 ? `${-r.days_left}d overdue` : r.days_left === 0 ? "today" : `in ${r.days_left}d`}</span>
    </td>
    <td className="px-3 py-2"><RefillLogSummary r={r} /></td>
    <td className="whitespace-nowrap px-3 py-2 text-right">
      <RefillNoteButton r={r} />
      <Button size="sm" variant="outline" className="h-7" title="Mark handled" aria-label="Mark handled" disabled={busy} onClick={() => onDone(r)} data-testid={`refill-done-${r.id}`}>
        <Check className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Handled</span></Button>
    </td>
  </tr>
);

export const RefillsDue = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["medications", "refills"], queryFn: async () => (await api.get("/medications/refills")).data });
  const done = useMutation({ mutationFn: (r) => api.post(`/medications/${r.id}/refill-handled`),
    onSuccess: (_, r) => { toast.success(`Refill for ${r.patient?.name} marked handled`); qc.invalidateQueries({ queryKey: ["medications"] }); qc.invalidateQueries({ queryKey: ["notifications"] }); },
    onError: () => toast.error("Could not update the refill") });
  if (!data) return null;
  const rows = [...data.due_soon, ...data.overdue];

  return (
    <div data-testid="refills-due" className="mt-6 rounded-xl border border-violet-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-4">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1F44]"><CalendarClock className="h-4 w-4 text-violet-600" />Refills due</span>
        <span data-testid="refills-due-soon" className="text-xs font-semibold text-amber-600">{data.due_soon.length} in the next {data.lead_days} days</span>
        <span data-testid="refills-overdue" className="text-xs font-semibold text-red-600">{data.overdue.length} overdue</span>
        {data.hidden_old > 0 && <span data-testid="refills-hidden-old" className="text-xs text-slate-500">{data.hidden_old} more than {data.max_overdue_days} days overdue hidden</span>}
        <Button size="sm" variant="outline" className="ml-auto h-7" disabled={!rows.length} onClick={() => exportCsv(rows)} data-testid="refills-export">
          <Download className="mr-1 h-3.5 w-3.5" />Call list CSV</Button>
        <span className="w-full text-xs text-slate-400">Refill date = {data.refill_days} days after each patient's newest note. For staff only — patients are not texted.</span>
      </div>
      {rows.length === 0 ? <p className="border-t border-slate-100 px-5 py-4 text-sm text-slate-500">No refills due right now.</p> : (
        <div className="max-h-80 overflow-auto border-t border-slate-100">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2">Patient</th><th className="hidden px-3 py-2 sm:table-cell">Phone</th><th className="px-3 py-2">Medicine</th><th className="hidden px-3 py-2 md:table-cell">Note date</th><th className="px-3 py-2">Refill</th><th className="px-3 py-2">Follow-up</th><th /></tr>
            </thead>
            <tbody>{rows.map((r) => <RefillRow key={r.id} r={r} busy={done.isPending} onDone={(x) => done.mutate(x)} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
